import React, { useRef } from "react";
import { motion } from "motion/react";
import {
    ChevronLeft, Sparkles, Search, FileText, Award,
    Building2, ShoppingBag, Briefcase, UtensilsCrossed,
    MapPin, PhoneCall,
} from "lucide-react";
import { TrustStrip } from "./ServicesMegaMenu";

// Desktop "خدمات SEO" mega-menu — a dedicated hub separate from "خدماتنا"
// because the SEO service alone now has enough real sub-pages (industry +
// city landing pages, an article hub, real case studies) to warrant its own
// navigable structure, per the user's own correction to the original brief:
// the nav item is named broadly ("خدمات SEO"), with the single real "شركة
// سيو" service page becoming one link inside it — not the menu's name
// itself — so dozens of future sectors/cities can be added here later
// without ever renaming the nav item again.
//
// Only 3 industries + 3 cities are real pages so far (pilot batch, see
// app/Support/SeoVariantCatalog.php) — every link below resolves to an
// actual page, none are placeholders. The remaining industries/cities will
// be appended to these two arrays as they ship in the follow-up pass; no
// structural change needed here when that happens.
const SEO_HUB_CATEGORIES = [
    {
        key: "core",
        title: "الخدمة الأساسية",
        icon: Search,
        viewAllHref: "/services/شركة-سيو",
        viewAllLabel: "صفحة الخدمة الرئيسية",
        items: [
            { name: "شركة سيو (الصفحة الرئيسية)", href: "/services/شركة-سيو", icon: Search },
            { name: "قصص نجاح حقيقية", href: "/case-studies", icon: Award },
            { name: "مقالات ودليل السيو", href: "/blogs?category=سيو", icon: FileText },
        ],
    },
    {
        key: "industries",
        title: "حسب القطاع",
        icon: Building2,
        viewAllHref: "/services/شركة-سيو",
        viewAllLabel: "عرض جميع القطاعات",
        items: [
            { name: "سيو المتاجر الإلكترونية", href: "/services/سيو-للمتاجر-الالكترونية", icon: ShoppingBag },
            { name: "سيو للشركات", href: "/services/سيو-للشركات", icon: Briefcase },
            { name: "سيو للمطاعم", href: "/services/سيو-للمطاعم", icon: UtensilsCrossed },
        ],
    },
    {
        key: "cities",
        title: "حسب المدينة",
        icon: MapPin,
        viewAllHref: "/services/شركة-سيو",
        viewAllLabel: "عرض جميع المدن",
        items: [
            { name: "أفضل شركة سيو في الرياض", href: "/services/سيو-الرياض", icon: MapPin },
            { name: "أفضل شركة سيو في جدة", href: "/services/سيو-جدة", icon: MapPin },
            { name: "أفضل شركة سيو في الدمام", href: "/services/سيو-الدمام", icon: MapPin },
        ],
    },
];

export { SEO_HUB_CATEGORIES };

const columnVariants = {
    hidden: { opacity: 0, y: 8 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.28, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] },
    }),
};

// Same anatomy as ServicesMegaMenu's CategoryColumn (fixed-height header,
// flex-1 item list, footer link pinned via margin-top:auto) — not imported
// from there since its footer label ("عرض جميع الخدمات") and icon-lookup
// convention are specific to that menu; icons here are embedded directly on
// each item instead of going through a separate string->component map,
// since this data isn't shared with anything outside this file's own
// desktop/mobile render paths.
const HubColumn = ({ category, index, onNavigate }) => {
    const CategoryIcon = category.icon;
    return (
        <motion.div
            custom={index}
            variants={columnVariants}
            initial="hidden"
            animate="visible"
            className="flex h-full min-w-0 flex-col"
        >
            <div className="mb-[18px] flex h-[40px] shrink-0 items-center gap-2">
                <h4 className="text-[18px] font-bold leading-none text-[#0F172A]">
                    {category.title}
                </h4>
                {CategoryIcon && (
                    <CategoryIcon
                        className="h-[18px] w-[18px] shrink-0 text-primary-600"
                        aria-hidden="true"
                    />
                )}
            </div>

            <ul className="flex flex-1 flex-col gap-3" role="none">
                {category.items.map((item) => {
                    const ItemIcon = item.icon;
                    return (
                        <li key={item.href} className="shrink-0" role="none">
                            <a
                                href={item.href}
                                onClick={onNavigate}
                                role="menuitem"
                                className="group/tile flex h-[38px] items-center justify-between gap-3 rounded-lg px-2 text-[15px] font-medium leading-[1.4] text-[#334155] transition-[background-color,color] duration-[250ms] ease-out hover:bg-[#F8FAFC] hover:text-primary-600 focus-visible:bg-[#F8FAFC] focus-visible:text-primary-600 focus-visible:outline-none"
                            >
                                <span className="min-w-0 flex-1 truncate">
                                    {item.name}
                                </span>
                                {ItemIcon && (
                                    <ItemIcon
                                        className="h-4 w-4 shrink-0 text-[#94A3B8] transition-[color,transform] duration-[250ms] ease-out group-hover/tile:scale-110 group-hover/tile:text-primary-600"
                                        aria-hidden="true"
                                    />
                                )}
                            </a>
                        </li>
                    );
                })}
            </ul>

            <a
                href={category.viewAllHref}
                onClick={onNavigate}
                className="mt-auto inline-flex h-[40px] shrink-0 items-center gap-1.5 border-t border-[#E2E8F0] whitespace-nowrap text-[12.5px] font-bold text-primary-600 transition-colors hover:text-primary-700"
            >
                {category.viewAllLabel}
                <ChevronLeft className="h-3 w-3" aria-hidden="true" />
            </a>
        </motion.div>
    );
};

const SeoCtaCard = ({ onNavigate }) => (
    <motion.div
        custom={SEO_HUB_CATEGORIES.length}
        variants={columnVariants}
        initial="hidden"
        animate="visible"
        className="group/cta relative order-first flex w-full flex-col overflow-hidden rounded-[20px] border border-primary-100/60 p-8 text-right transition-shadow duration-300 lg:order-none lg:w-[320px] lg:shrink-0"
        style={{
            background: "linear-gradient(135deg, #F8FAFF, #EEF4FF)",
            boxShadow: "0 20px 60px rgba(37,99,235,0.12)",
        }}
    >
        <span
            className="pointer-events-none absolute -top-12 -left-12 h-40 w-40 rounded-full bg-primary-200/30 blur-3xl transition-transform duration-700 group-hover/cta:scale-110"
            aria-hidden="true"
        />
        <span
            className="pointer-events-none absolute bottom-6 left-6 h-16 w-16 rounded-full bg-primary-300/20 blur-2xl"
            aria-hidden="true"
        />

        <span className="relative mb-5 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/80 px-3.5 py-1.5 text-[11px] font-bold text-primary-600">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            استشارة سيو مجانية
        </span>

        <h4 className="relative mb-2.5 text-[20px] font-bold leading-snug text-[#0F172A]">
            نتائج سيو حقيقية، لا وعود
        </h4>
        <p className="relative mb-7 text-[13.5px] font-medium leading-relaxed text-[#64748B]">
            راجع قصص نجاح فعلية لعملائنا، ثم احجز استشارة مجانية لمعرفة كيف يمكن لموقعك أن يتصدر نتائج البحث.
        </p>

        <div className="relative mt-auto flex w-full flex-col gap-2.5">
            <a
                href="https://api.whatsapp.com/send/?phone=966569691027"
                target="_blank"
                rel="noopener noreferrer"
                onClick={onNavigate}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary-600 px-4 py-3 text-[13.5px] font-bold text-white shadow-[0_10px_24px_rgba(37,99,235,0.25)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-700 hover:shadow-[0_14px_30px_rgba(37,99,235,0.32)]"
            >
                <PhoneCall className="h-3.5 w-3.5" aria-hidden="true" />
                احجز استشارتك المجانية
            </a>
            <a
                href="/case-studies"
                onClick={onNavigate}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary-200 bg-white/60 px-4 py-2.5 text-[13.5px] font-bold text-primary-600 transition-colors duration-300 hover:bg-white"
            >
                <Award className="h-3.5 w-3.5" aria-hidden="true" />
                شاهد قصص النجاح
            </a>
        </div>
    </motion.div>
);

// `onClose` fires on link click (mouse) and on Escape/outside-click, wired
// by the parent Header — identical contract to ServicesMegaMenu so Header.jsx
// treats both dropdowns the same way.
const SeoMegaMenu = React.forwardRef(({ onClose }, ref) => {
    const fallbackRef = useRef(null);
    const panelRef = ref || fallbackRef;

    return (
        <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            role="menu"
            aria-label="خدمات SEO"
            className="fixed left-1/2 top-[92px] z-40 max-h-[80vh] w-[min(94vw,1200px)] -translate-x-1/2 overflow-y-auto rounded-[24px] bg-white"
            style={{ boxShadow: "0 40px 120px rgba(15,23,42,0.12)" }}
        >
            <div className="px-8 py-10 sm:px-10 lg:px-12">
                {/* DOM order is [columns..., CTA] so under dir="rtl" the core
                    column lands on the right (read first) and the CTA lands
                    on the far left — same convention as ServicesMegaMenu. */}
                <div className="flex flex-col-reverse gap-10 text-right lg:flex-row lg:items-stretch lg:gap-10">
                    <div className="grid flex-1 grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-3">
                        {SEO_HUB_CATEGORIES.map((category, i) => (
                            <HubColumn
                                key={category.key}
                                category={category}
                                index={i}
                                onNavigate={onClose}
                            />
                        ))}
                    </div>
                    <SeoCtaCard onNavigate={onClose} />
                </div>

                <TrustStrip />
            </div>
        </motion.div>
    );
});

SeoMegaMenu.displayName = "SeoMegaMenu";

export default SeoMegaMenu;
